import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useMemo, useState, type ReactNode } from "react";
import { optionMatchesQuery } from "./comboboxFilter";

// Sentinel value for the empty-state "add what you typed" row. It is built
// fresh from the live query on every render and is never a real option, so
// it can never collide with an actual option value.
const EMPTY_ACTION_VALUE = "__a2z_combobox_empty_action__";
const CLEAR_OPTION_VALUE = "__a2z_combobox_clear__";

type Option = {
  value: string;
  label: string;
  /** Keep an unavailable option visible without allowing it to be selected. */
  disabled?: boolean;
  /**
   * Extra strings this option should ALSO match on, beyond its visible label.
   *
   * Exists for bilingual catalogues: a governorate/area list renders one
   * localized name, so on an Arabic page only the Arabic name was searchable
   * and on an English page only the English one. Egyptian shoppers type either
   * — "Giza" on an Arabic page, "الجيزة" on an English one — and got an empty
   * list for a place that is right there in the catalogue.
   *
   * Never rendered; matching only. Options that omit it behave exactly as
   * before.
   */
  searchAliases?: string[];
};

type BaseProps = {
  options: Option[];
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Label shown when search returns no results (default: "No results") */
  noResultsLabel?: string;
  /** Template for multi-select count, use {count} placeholder (default: "{count} selected") */
  selectedCountLabel?: string;
  /**
   * Optional normalizer applied to both the option label and the query before
   * comparing them, in addition to the default lowercase/includes match.
   * Opt-in only — when omitted, filtering behavior is unchanged.
   */
  normalize?: (value: string) => string;
  /**
   * Which side of the input the options panel opens toward. Defaults to
   * `"bottom"` (unchanged behavior for every existing caller).
   *
   * The panel is a floating-ui anchor, which only flips for VIEWPORT edges —
   * it has no idea a sibling like a dialog's footer sits right below the
   * input in normal flow, so it can portal straight over that sibling and
   * swallow its clicks. `"top"` is for exactly that case: a combobox placed
   * directly above a footer/CTA row, where opening upward (into the dialog's
   * own empty space) is the fix rather than fighting z-index.
   */
  optionsPlacement?: "top" | "bottom";
  /**
   * Test hook, rendered on the control's own wrapper element.
   *
   * Declared because these props are a closed object type and nothing spreads
   * the rest onto an element: a `data-testid` passed by a caller was dropped
   * silently, so the attribute never reached the DOM and any e2e keyed on it
   * matched nothing and passed vacuously. One optional attribute on an
   * element that already renders; the API is otherwise unchanged.
   */
  "data-testid"?: string;
};

type SingleProps = BaseProps & {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Opt-in. When true and the query is empty, no options are rendered —
   * instead `searchFirstHint` (falling back to `noResultsLabel`) is shown,
   * prompting the user to type. Omitted (default false/undefined): behavior
   * is unchanged, the full option list renders immediately.
   */
  searchFirst?: boolean;
  /** Hint shown when `searchFirst` is true and the query is empty. */
  searchFirstHint?: string;
  /**
   * Opt-in. Renders a "none" choice as the FIRST listbox row whenever a value
   * is selected. Selecting it calls `onChange(null)`. The none state is one of
   * the mutually exclusive options, so it belongs INSIDE the listbox —
   * arrow-key reachable and announced — not as a detached link under the
   * input (which reads as a suggestion and shifts layout as it appears).
   */
  clearOption?: { label: string };
  /**
   * Opt-in. When the filtered list is empty AND the query is non-empty,
   * renders this as an extra, keyboard-accessible, selectable row (same
   * highlight/hover affordances as a normal option) built from the live
   * query text. Selecting it calls `onSelect` with that query instead of
   * the normal `onChange`. Omitted (default): the plain `noResultsLabel`
   * message renders, exactly as today.
   */
  emptyAction?: {
    render: (query: string) => ReactNode;
    onSelect: (query: string) => void;
  };
  /**
   * Fires with the raw input text on every keystroke, independent of
   * `onChange` (which only fires when an option is actually picked).
   *
   * Exists so a caller over a fixed, closed option list — a picker in
   * disguise as a text field — can tell "the cashier is typing something
   * that isn't one of the options" from "an option is selected", and clear
   * its own selection rather than let a stale pick silently outlive a typed
   * query that no longer names it. Opt-in; omitted, nothing changes.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Opt-in. Draws an option's row content instead of its bare label — a
   * picker whose choices are recognised by a picture as much as a name (a
   * dish's photo or badge beside its name). Matching still runs on `label`
   * and `searchAliases`; the row stays the same keyboard-reachable
   * `ComboboxOption`. Omitted: the label and the selected check, as before.
   */
  renderOption?: (option: Option, state: { selected: boolean }) => ReactNode;
};

type MultiProps = BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type ComboboxProps = SingleProps | MultiProps;

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-[color:var(--text-muted)]"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SingleCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  normalize,
  searchFirst,
  searchFirstHint,
  emptyAction,
  clearOption,
  optionsPlacement = "bottom",
  onQueryChange,
  renderOption,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: SingleProps) {
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const showSearchFirstHint = Boolean(searchFirst) && trimmedQuery.length === 0;

  const filtered = useMemo(() => {
    if (showSearchFirstHint) return [];
    if (!query) return options;
    return options.filter((o) => optionMatchesQuery(o, query, normalize));
  }, [options, query, normalize, showSearchFirstHint]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const showEmptyAction =
    !showSearchFirstHint &&
    filtered.length === 0 &&
    trimmedQuery.length > 0 &&
    Boolean(emptyAction);
  const emptyActionOption: Option | null = showEmptyAction
    ? { value: EMPTY_ACTION_VALUE, label: query }
    : null;

  return (
    <HeadlessCombobox
      value={selectedOption}
      onChange={(opt: Option | null) => {
        if (opt?.value === CLEAR_OPTION_VALUE) {
          onChange(null);
          return;
        }
        if (opt?.value === EMPTY_ACTION_VALUE) {
          // Trim before handing the query out: consumers persist this text
          // (and validate its length), so leading/trailing whitespace from
          // typing would otherwise be stored and counted verbatim.
          emptyAction?.onSelect(query.trim());
          return;
        }
        onChange(opt?.value ?? null);
      }}
      disabled={disabled}
      immediate
    >
      <div
        className={`relative w-full ${className ?? ""}`}
        data-testid={testId}
      >
        <div
          data-ui="select"
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            displayValue={(opt: Option | null) => opt?.label ?? ""}
            onChange={(e) => {
              setQuery(e.target.value);
              onQueryChange?.(e.target.value);
            }}
            placeholder={placeholder}
            aria-label={ariaLabel}
          />
          {/* The chevron is a BUTTON, and until now it was an unnamed one — the
              input beside it took `aria-label` and this took nothing. A screen
              reader reached it as "button", with no clue which field it opened,
              and an interaction probe found it nine times across five screens:
              the language switcher, four invoice filters and the shift
              movement-category picker. It was the single most repeated unnamed
              control in the restaurant.

              It borrows the field's own name rather than inventing a second
              string to keep in sync: the field is what the button opens, so
              "Category, button" is the correct reading. `undefined` when the
              caller passes no label, which leaves every existing call site
              exactly as it was — additively, since this is a shared primitive
              that seller, admin and buyer all render. */}
          <ComboboxButton
            className="flex h-full items-center px-3"
            aria-label={ariaLabel}
          >
            <ChevronIcon />
          </ComboboxButton>
        </div>
        {/* Anchored PORTAL (Headless UI v2): escapes any parent overflow
            container. With anchor={false} the panel rendered in-flow inside a
            Modal's scrollable body — inflating the modal's scroll height,
            getting clipped, and adding a third nested scrollbar. Portaled it
            overlays, flips when out of room, and caps against the viewport.
            z-[80] clears the elevated-modal layer (z-[70]). */}
        <ComboboxOptions
          className="z-[80] w-[var(--input-width)] [--anchor-max-height:40vh] max-h-60 overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={{
            to: optionsPlacement === "top" ? "top start" : "bottom start",
            gap: 4,
          }}
        >
          {/* KEEP THESE AS TWO SIBLING CHILDREN — do not merge them back under
              one `<>…</>`.

              Headless UI wraps the panel's children in its internal `Frozen`
              (a `forwardRef`) which does `cloneElement(child, { ref })` when
              the children are a SINGLE valid element. A `<>…</>` is a single
              valid element, so the clone lands a `ref` prop on a Fragment and
              React 19 logs "Invalid prop `ref` supplied to `React.Fragment`"
              on every reconcile of an open panel. Two children make
              `props.children` an array, which `Frozen` passes through
              untouched. (The ref it forwards is always null — the warning is
              noise, not a dropped ref — but it is ~6 lines per panel render.)

              The clear row keeps the exact conditions it had inside the
              fragment, so nothing renders that did not render before. */}
          {!showSearchFirstHint &&
          filtered.length > 0 &&
          clearOption &&
          value !== null ? (
            <ComboboxOption
              value={{ value: CLEAR_OPTION_VALUE, label: clearOption.label }}
              className="flex min-h-11 cursor-pointer items-center gap-2 border-b border-[color:var(--border-light)] px-4 py-2.5 text-sm text-[color:var(--text-muted)] data-[focus]:bg-[color:var(--background-muted)]"
              data-testid="combobox-clear-option"
            >
              <span className="flex-1 truncate">{clearOption.label}</span>
            </ComboboxOption>
          ) : null}
          {showSearchFirstHint ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {searchFirstHint ?? noResultsLabel}
            </div>
          ) : filtered.length === 0 ? (
            emptyActionOption ? (
              <ComboboxOption
                key={emptyActionOption.value}
                value={emptyActionOption}
                // min-h-11 (44px): this row is the only route into the
                // free-text flow, and it appears exactly when the buyer's
                // search failed — an undersized target here dead-ends them
                // on mobile (44px touch-target rule, buyer-ux-spec-v2 §mobile).
                className="flex min-h-11 cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)]"
              >
                {emptyAction?.render(query)}
              </ComboboxOption>
            ) : (
              <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
                {noResultsLabel}
              </div>
            )
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                disabled={option.disabled}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)] data-[selected]:font-medium data-[disabled]:cursor-not-allowed data-[disabled]:text-[color:var(--text-muted)] data-[disabled]:opacity-60"
              >
                {({ selected }) =>
                  renderOption ? (
                    <>{renderOption(option, { selected })}</>
                  ) : (
                    <>
                      <span className="flex-1 truncate">{option.label}</span>
                      {selected && (
                        <span className="text-[color:var(--primary-500)]">
                          <CheckIcon />
                        </span>
                      )}
                    </>
                  )
                }
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

function MultiCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  selectedCountLabel = "{count} selected",
  normalize,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: MultiProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return options;
    const lower = query.toLowerCase();
    if (normalize) {
      const normalizedQuery = normalize(query);
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(lower) ||
          normalize(o.label).includes(normalizedQuery),
      );
    }
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query, normalize]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const summaryText = useMemo(() => {
    if (selectedOptions.length === 0) return "";
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return selectedCountLabel.replace(
      "{count}",
      String(selectedOptions.length),
    );
  }, [selectedOptions, selectedCountLabel]);

  return (
    <HeadlessCombobox
      value={selectedOptions}
      onChange={(opts: Option[]) => onChange(opts.map((o) => o.value))}
      multiple
      disabled={disabled}
      by="value"
      immediate
    >
      <div
        className={`relative w-full ${className ?? ""}`}
        data-testid={testId}
      >
        <div
          data-ui="select"
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            placeholder={
              selectedOptions.length === 0 ? placeholder : summaryText
            }
            aria-label={ariaLabel}
          />
          {/* Named for the same reason as the single-select twin above, and in
              the same way — see that comment. Two call sites, one defect. */}
          <ComboboxButton
            className="flex h-full items-center px-3"
            aria-label={ariaLabel}
          >
            <ChevronIcon />
          </ComboboxButton>
        </div>
        {/* Anchored PORTAL (Headless UI v2): escapes any parent overflow
            container. With anchor={false} the panel rendered in-flow inside a
            Modal's scrollable body — inflating the modal's scroll height,
            getting clipped, and adding a third nested scrollbar. Portaled it
            overlays, flips when out of room, and caps against the viewport.
            z-[80] clears the elevated-modal layer (z-[70]). */}
        <ComboboxOptions
          className="z-[80] w-[var(--input-width)] [--anchor-max-height:40vh] max-h-60 overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={{ to: "bottom start", gap: 4 }}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {noResultsLabel}
            </div>
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                disabled={option.disabled}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)] data-[disabled]:cursor-not-allowed data-[disabled]:text-[color:var(--text-muted)] data-[disabled]:opacity-60"
              >
                {({ selected }) => (
                  <>
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={option.disabled}
                      readOnly
                      className="h-4 w-4 rounded border-[color:var(--border-light)] accent-[color:var(--primary-500)]"
                      tabIndex={-1}
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

export function Combobox(props: ComboboxProps) {
  if (props.multiple) {
    return <MultiCombobox {...props} />;
  }
  return <SingleCombobox {...props} />;
}

export type { ComboboxProps, Option as ComboboxOption };
